import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Receipt as ReceiptIcon, FileDown, FileText, Loader2, Calendar } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(v);
    } catch {
        return `${v} ${cur}`;
    }
}

function monthLabel(date) {
    return new Date(date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function pad2(n) {
    return String(n).padStart(2, "0");
}

function isoDay(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function monthRange(year, month /* 0-11 */) {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { startDate: isoDay(start), endDate: isoDay(end) };
}

function buildMonthOptions(months = 12) {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < months; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const { startDate, endDate } = monthRange(d.getFullYear(), d.getMonth());
        opts.push({
            id: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`,
            label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
            startDate,
            endDate,
        });
    }
    return opts;
}

export default function History() {
    const [items, setItems] = useState([]);

    // Export panel state
    const monthOptions = useMemo(() => buildMonthOptions(12), []);
    const [mode, setMode] = useState("month"); // 'month' | 'custom'
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].id);
    const [startDate, setStartDate] = useState(monthOptions[0].startDate);
    const [endDate, setEndDate] = useState(monthOptions[0].endDate);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [downloading, setDownloading] = useState(null); // 'pdf' | 'csv' | null

    useEffect(() => {
        api.get("/receipts", { params: { limit: 200 } }).then((r) => setItems(r.data));
        // Run once on mount — all refs are stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effective range based on mode
    const range = useMemo(() => {
        if (mode === "month") {
            const opt = monthOptions.find((o) => o.id === selectedMonth) || monthOptions[0];
            return { startDate: opt.startDate, endDate: opt.endDate };
        }
        return { startDate, endDate };
    }, [mode, selectedMonth, monthOptions, startDate, endDate]);

    // Fetch preview whenever the range changes
    const loadPreview = useCallback(async () => {
        if (!range.startDate || !range.endDate) return;
        setPreviewLoading(true);
        try {
            const { data } = await api.get("/reports/preview", {
                params: { start_date: range.startDate, end_date: range.endDate },
            });
            setPreview(data);
        } catch (e) {
            setPreview(null);
            const msg = e?.response?.data?.detail || "Impossible de calculer l'aperçu";
            toast.error(msg);
        } finally {
            setPreviewLoading(false);
        }
    }, [range.startDate, range.endDate]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    async function download(format) {
        if (!range.startDate || !range.endDate) {
            toast.error("Sélectionnez une période valide");
            return;
        }
        setDownloading(format);
        try {
            const token = localStorage.getItem("tk_token");
            const url = `${API}/reports/export?format=${format}&start_date=${range.startDate}&end_date=${range.endDate}`;
            const res = await fetch(url, {
                method: "GET",
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!res.ok) {
                let detail = `Erreur ${res.status}`;
                try {
                    const j = await res.json();
                    detail = j?.detail || detail;
                } catch (parseErr) {
                    console.debug("reports export error response not JSON", parseErr);
                }
                throw new Error(detail);
            }
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="?([^";]+)"?/i);
            const filename =
                m?.[1] || `ticketify-rapport-${range.startDate}-au-${range.endDate}.${format}`;
            const link = document.createElement("a");
            const objectUrl = window.URL.createObjectURL(blob);
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
            toast.success(`Rapport ${format.toUpperCase()} téléchargé`);
        } catch (e) {
            toast.error(e.message || "Échec du téléchargement");
        } finally {
            setDownloading(null);
        }
    }

    const groups = useMemo(() => {
        const g = new Map();
        for (const r of items) {
            const d = r.purchase_date || r.created_at;
            const key = d ? `${new Date(d).getFullYear()}-${new Date(d).getMonth() + 1}` : "—";
            if (!g.has(key)) g.set(key, { label: monthLabel(d), items: [], total: 0 });
            const entry = g.get(key);
            entry.items.push(r);
            entry.total += Number(r.total_amount || 0);
        }
        return Array.from(g.values());
    }, [items]);

    const isEmptyRange =
        preview && preview.tx_count === 0 && preview.receipts_count === 0;

    return (
        <div className="max-w-4xl mx-auto px-6 sm:px-8 space-y-8">
            <div className="tk-rise">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Chronologie</p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                    Historique
                </h1>
            </div>

            {/* Export panel */}
            <section
                data-testid="reports-export-panel"
                className="tk-card tk-rise tk-rise-1 p-6 sm:p-8 space-y-6"
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">
                            Export comptable
                        </p>
                        <h2 className="mt-1 text-xl sm:text-2xl font-semibold text-[#1D1D1F]">
                            Rapport mensuel
                        </h2>
                        <p className="text-sm text-[#86868B] mt-1">
                            Transactions, justificatifs, TVA déductible et totaux par catégorie — prêt pour la
                            compta.
                        </p>
                    </div>
                    <Calendar className="w-6 h-6 text-[#86868B] shrink-0" />
                </div>

                {/* Mode tabs */}
                <div className="inline-flex p-1 rounded-full bg-[#F5F5F7] border border-black/5">
                    <button
                        type="button"
                        data-testid="reports-mode-month"
                        onClick={() => setMode("month")}
                        className={`px-4 py-1.5 text-sm rounded-full transition ${
                            mode === "month"
                                ? "bg-white text-[#1D1D1F] shadow-sm"
                                : "text-[#86868B] hover:text-[#1D1D1F]"
                        }`}
                    >
                        Par mois
                    </button>
                    <button
                        type="button"
                        data-testid="reports-mode-custom"
                        onClick={() => setMode("custom")}
                        className={`px-4 py-1.5 text-sm rounded-full transition ${
                            mode === "custom"
                                ? "bg-white text-[#1D1D1F] shadow-sm"
                                : "text-[#86868B] hover:text-[#1D1D1F]"
                        }`}
                    >
                        Période libre
                    </button>
                </div>

                {/* Selectors */}
                {mode === "month" ? (
                    <div>
                        <label className="text-xs font-medium uppercase tracking-wider text-[#86868B]">
                            Mois
                        </label>
                        <select
                            data-testid="reports-month-select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="tk-input mt-2 capitalize"
                        >
                            {monthOptions.map((o) => (
                                <option key={o.id} value={o.id} className="capitalize">
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium uppercase tracking-wider text-[#86868B]">
                                Du
                            </label>
                            <input
                                type="date"
                                data-testid="reports-start-date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="tk-input mt-2"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase tracking-wider text-[#86868B]">
                                Au
                            </label>
                            <input
                                type="date"
                                data-testid="reports-end-date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                min={startDate}
                                className="tk-input mt-2"
                            />
                        </div>
                    </div>
                )}

                {/* Preview KPIs */}
                <div
                    data-testid="reports-preview"
                    className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                >
                    <KPI label="Transactions" value={preview?.tx_count ?? "—"} loading={previewLoading} />
                    <KPI
                        label="Justificatifs OK"
                        value={preview?.found ?? "—"}
                        accent="green"
                        loading={previewLoading}
                    />
                    <KPI
                        label="Manquants"
                        value={preview?.missing ?? "—"}
                        accent={(preview?.missing ?? 0) > 0 ? "red" : "muted"}
                        loading={previewLoading}
                    />
                    <KPI
                        label="TVA déductible"
                        value={preview ? fmtMoney(preview.total_vat) : "—"}
                        loading={previewLoading}
                    />
                </div>

                {/* Range summary */}
                {preview && (
                    <p className="text-sm text-[#86868B]" data-testid="reports-preview-summary">
                        Du <span className="text-[#1D1D1F]">{new Date(preview.start_date).toLocaleDateString("fr-FR")}</span> au{" "}
                        <span className="text-[#1D1D1F]">{new Date(preview.end_date).toLocaleDateString("fr-FR")}</span>
                        {" · "}
                        <span className="text-[#1D1D1F]">{preview.receipts_count}</span> reçus
                        {" · "}
                        <span className="text-[#1D1D1F]">{fmtMoney(preview.total_tx_amount)}</span> dépensés
                        {" · "}
                        Taux d&apos;automation <span className="text-[#1D1D1F]">{preview.automation_rate}%</span>
                    </p>
                )}

                {isEmptyRange && (
                    <p className="text-sm text-[#86868B]" data-testid="reports-empty-warn">
                        Aucune donnée sur cette période — vous pouvez quand même générer un rapport vide.
                    </p>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                        data-testid="reports-download-pdf"
                        onClick={() => download("pdf")}
                        disabled={downloading !== null}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-[#1D1D1F] text-white font-medium hover:bg-black transition disabled:opacity-60"
                    >
                        {downloading === "pdf" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileText className="w-4 h-4" />
                        )}
                        Télécharger le PDF
                    </button>
                    <button
                        data-testid="reports-download-csv"
                        onClick={() => download("csv")}
                        disabled={downloading !== null}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white text-[#1D1D1F] font-medium border border-black/10 hover:bg-[#F5F5F7] transition disabled:opacity-60"
                    >
                        {downloading === "csv" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileDown className="w-4 h-4" />
                        )}
                        Télécharger le CSV
                    </button>
                </div>
            </section>

            {/* Existing month-grouped history */}
            {groups.length === 0 ? (
                <div className="tk-card p-12 text-center text-[#86868B]" data-testid="history-empty">
                    Aucun ticket pour l&apos;instant.
                </div>
            ) : (
                <div className="space-y-10">
                    {groups.map((g, idx) => (
                        <div key={g.label} className="tk-rise" data-testid={`history-group-${idx}`}>
                            <div className="flex items-baseline justify-between mb-4 px-2">
                                <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#86868B] capitalize">
                                    {g.label}
                                </h3>
                                <span className="text-sm text-[#86868B]">Total : {fmtMoney(g.total)}</span>
                            </div>
                            <div className="tk-card divide-y divide-black/5 overflow-hidden">
                                {g.items.map((r) => (
                                    <Link
                                        to={`/app/receipt/${r.receipt_id}`}
                                        key={r.receipt_id}
                                        className="flex items-center gap-4 p-5 hover:bg-[#F5F5F7] transition"
                                        data-testid={`history-row-${r.receipt_id}`}
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center">
                                            <ReceiptIcon className="w-5 h-5 text-[#1D1D1F]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-[#1D1D1F] truncate">
                                                {r.merchant || "Ticket"}
                                            </p>
                                            <p className="text-sm text-[#86868B] capitalize">{r.category}</p>
                                        </div>
                                        <p className="font-semibold text-[#1D1D1F]">
                                            {fmtMoney(r.total_amount, r.currency)}
                                        </p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function KPI({ label, value, accent = "default", loading }) {
    const tone =
        accent === "green"
            ? "text-[#34C759]"
            : accent === "red"
            ? "text-[#FF3B30]"
            : accent === "muted"
            ? "text-[#86868B]"
            : "text-[#1D1D1F]";
    return (
        <div className="rounded-2xl bg-[#F5F5F7] border border-black/5 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#86868B]">{label}</p>
            <p className={`mt-1 text-xl font-semibold ${tone}`}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : value}
            </p>
        </div>
    );
}
